export const PROPOSED_REVISIONS_AUTHOR = "Proposed by this redline";

/**
 * The highlight a card paints behind the words of its own proposal, in every
 * preview of it: the live card, the applied documentation, the editor's
 * tokens and the legend's swatch. The words keep the text colour of the
 * paragraph, so a proposal reads as highlighted text and never as text in an
 * author's colour, which is how every other author's revision is drawn.
 */
export const PROPOSED_REVISIONS_HIGHLIGHT = "#FEF9C3";

const REVISION_TAGS = new Set(["w:ins", "w:del", "w:moveTo", "w:moveFrom"]);

export class ProposedRevisions {
    static relabel({ packageOoxml, stampedAt }: { packageOoxml: string; stampedAt: string }): string {
        const parsed = new DOMParser().parseFromString(packageOoxml, "application/xml");
        const parserErrorsFound = parsed.getElementsByTagName("parsererror").length > 0;
        /**
         * WHEN the OOXML does not parse as XML
         *   CONSEQUENCE
         *     return packageOoxml unchanged.
         */
        if (parserErrorsFound) return packageOoxml;
        for (const node of Array.from(parsed.getElementsByTagName("*"))) {
            const nodeIsRevisionTag = REVISION_TAGS.has(node.nodeName);
            /**
             * WHEN node.nodeName is not in REVISION_TAGS
             *   CONSEQUENCE
             *     skip this node.
             */
            if (!nodeIsRevisionTag) continue;
            const nodeWasStampedThisPass = node.getAttribute("w:date") === stampedAt;
            /**
             * WHEN the node's w:date is not stampedAt
             *   CONSEQUENCE
             *     skip this revision; only this pass's stamped revisions are relabeled.
             */
            if (!nodeWasStampedThisPass) continue;
            node.setAttribute("w:author", PROPOSED_REVISIONS_AUTHOR);
        }
        return new XMLSerializer().serializeToString(parsed);
    }

    /**
     * The same relabel for a card's own markup, whose revisions were stamped when
     * the card was proposed rather than by a preview pass: the newest `w:date` in
     * the package is the proposal's stamp, and every revision carrying it reads
     * "Proposed by this redline". Older revisions, tracked changes the section
     * already held, keep their authors.
     *
     * WHY
     *   A new-paragraph card previews the markup the core sent. Without this its
     *   legend named the signed-in user ("James Philhower") as the author of a
     *   change nobody had made yet, while every text card's legend said
     *   "Proposed by this redline".
     *
     * EXAMPLE
     *   w:ins  w:date="2026-09-25T13:35:29Z"  (the proposed paragraph)  -> "Proposed by this redline"
     *   w:del  w:date="2026-09-20T09:00:00Z"  (an edit already in Word)  -> keeps its author
     *
     * RETURNS
     *   The package unchanged when it does not parse or holds no dated revision.
     */
    static relabelNewestStamp(packageOoxml: string): string {
        const parsed = new DOMParser().parseFromString(packageOoxml, "application/xml");
        /**
         * WHEN the OOXML does not parse as XML
         *   CONSEQUENCE
         *     return packageOoxml unchanged.
         */
        if (parsed.getElementsByTagName("parsererror").length > 0) return packageOoxml;
        const revisionStamps = Array.from(parsed.getElementsByTagName("*"))
            .filter((node) => REVISION_TAGS.has(node.nodeName))
            .map((node) => node.getAttribute("w:date"))
            .filter((stamp): stamp is string => stamp !== null && stamp.length > 0);
        /**
         * WHEN no revision carries a date
         *   CONSEQUENCE
         *     nothing marks the proposal's revisions apart: the package is returned unchanged.
         */
        if (revisionStamps.length === 0) return packageOoxml;
        /**
         * WHY a string comparison
         *   Word writes every w:date as the same ISO-8601 shape ("2026-09-25T13:35:29Z"),
         *   which sorts by time when compared as text.
         * EXAMPLE
         *   ["2026-09-20T09:00:00Z", "2026-09-25T13:35:29Z"] -> "2026-09-25T13:35:29Z"
         */
        const newestStamp = revisionStamps.reduce((newest, stamp) => (stamp > newest ? stamp : newest));
        return ProposedRevisions.relabel({ packageOoxml, stampedAt: newestStamp });
    }
}
